/**
 *
 * FNAnalysisFNStatementsCashFlow
 *
 */

import React from "react";
import HelpIconButtonWithTooltipAndTypeformModal from "../../typeform/HelpIconButtonWithTooltipAndTypeformModal";
import TypeformContent from "../../typeform/TypeformContent";

// Components

/**
 * Price in millions.
 */
const PriceInMillions = ({ price, baseClassName }) => {
  if (typeof price !== "number") {
    return <div className={baseClassName}>n/a</div>;
  }

  let newPrice;
  if (price < 0) {
    newPrice = (price / 1000000).toFixed(2) * -1;
    return (
      <div className={`red ${baseClassName}`}>{`-$${newPrice}M`}</div>
    );
  } else {
    newPrice = (price / 1000000).toFixed(2);
    return <div className={baseClassName}>{`$${newPrice}M`}</div>;
  }
};

/**
 * Table Head
 */

const TableHead = ({ current_year }) => (
  <thead>
    <tr>
      <th className="table-title text-center">
        <span />
      </th>
      <th className="table-title title-gray text-center">
        YEAR {current_year}
      </th>
    </tr>
  </thead>
);

/**
 * Table row contents
 */

const TableRowContentWhite = ({ cashFlow, title }) => (
  <tr className="text-center">
    <th className="description">{title ? title : cashFlow.name}</th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={cashFlow.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row contents with gray background color.
 */

const TableRowContentGray = ({ cashFlow }) => (
  <tr className="text-center bg-gray none-shadow">
    <th className="description">{cashFlow.name}</th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={cashFlow.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row contents with dim gray background color.
 */

const TableRowContentDimGray = ({ cashFlow }) => (
  <tr className="text-center dim-gray none-shadow">
    <th className="description">
      <strong>{cashFlow.name}</strong>
    </th>
    <td>
      <PriceInMillions
        baseClassName="text-center"
        price={cashFlow.data.value}
      />
    </td>
  </tr>
);

/**
 * Table row title.
 */

const TableRowContentHeader = ({ title }) => (
  <tr className="text-center">
    <th className="sub-title title-sky-blue">{title}</th>
    <td>
      <div className="row flex">
        <div className="col-xs-6" />
        <div className="col-xs-6" />
        <div className="col-xs-6" />
      </div>
    </td>
  </tr>
);

/**
 * Table row sub header.
 */

const TableRowContentSubheader = ({ title }) => (
  <tr className="text-center">
    <th className="sub-title">{title}</th>
    <td>
      <div className="row flex">
        <div className="col-xs-6" />
        <div className="col-xs-6" />
        <div className="col-xs-6" />
      </div>
    </td>
  </tr>
);

/**
 *
 * FNAnalysisFNStatementsCashFlow
 *
 */
function CashFlowWidget({
  cashFlow,
  current_year,
  className = "team-reports-with-scroll"
}) {
  const {
    beginningCash,
    capitalExpenditurePurchases,
    capitalExpenditureSales,
    cashpaymentforIncomeTaxes,
    cashpaymentforInterests,
    cashpaymentsforOperatingexpenses,
    cashpaymentsforPurchases,
    cashpaymentsforWages,
    cashreceivedfromCustomers,
    debtBorrowed,
    debtRepaid,
    dividends,
    endingCash,
    investmentPurchases,
    investmentSales,
    netChangeinCash,
    stockIssuance,
    stockRepurchase,
    totalCashFlowsfromFinancing,
    totalCashFlowsfromInvesting,
    totalCashFlowsfromOperations
  } = cashFlow;

  return (
    <div className={`panel-dashboard-white-full ${className}`}>
      <h5 className="title-sky-blue">
        Cash Flows
        <HelpIconButtonWithTooltipAndTypeformModal
          iconSize="sm"
          {...TypeformContent.CASH_FLOWS}
        />
      </h5>
      <div className="table-responsive fn-results-table">
        <table className="table border-analysis mb-20">
          <TableHead current_year={current_year} />
          {Object.keys(beginningCash.data).length > 0 ||
            Object.keys(capitalExpenditurePurchases.data).length > 0 ||
            Object.keys(capitalExpenditureSales.data).length > 0 ? (
            <tbody>
              <TableRowContentSubheader title="Cash Flows From Operations:" />
              <TableRowContentGray cashFlow={cashreceivedfromCustomers} />
              <TableRowContentWhite
                cashFlow={cashpaymentsforPurchases}
                title="Cash payment for Suppliers"
              />
              <TableRowContentGray cashFlow={cashpaymentsforWages} />
              <TableRowContentWhite
                cashFlow={cashpaymentsforOperatingexpenses}
                title="Cash payment for Operating expenses"
              />
              <TableRowContentGray cashFlow={cashpaymentforInterests} />
              <TableRowContentWhite cashFlow={cashpaymentforIncomeTaxes} />
              <TableRowContentDimGray cashFlow={totalCashFlowsfromOperations} />

              <TableRowContentSubheader title="" />
              <TableRowContentSubheader title="Cash Flows From Investing:" />
              <TableRowContentGray cashFlow={capitalExpenditurePurchases} />
              <TableRowContentWhite cashFlow={capitalExpenditureSales} />
              <TableRowContentGray cashFlow={investmentPurchases} />
              <TableRowContentWhite cashFlow={investmentSales} />
              <TableRowContentDimGray cashFlow={totalCashFlowsfromInvesting} />

              <TableRowContentSubheader title="" />
              <TableRowContentSubheader title="Cash Flow From Financing:" />
              <TableRowContentGray cashFlow={stockIssuance} />
              <TableRowContentWhite cashFlow={stockRepurchase} />
              <TableRowContentGray cashFlow={dividends} />
              <TableRowContentWhite cashFlow={debtBorrowed} />
              <TableRowContentGray cashFlow={debtRepaid} />
              <TableRowContentDimGray cashFlow={totalCashFlowsfromFinancing} />
              <TableRowContentHeader title="" />
              <TableRowContentHeader title="Change In Cash" />
              <TableRowContentWhite cashFlow={beginningCash} />
              <TableRowContentGray cashFlow={endingCash} />
              <TableRowContentDimGray cashFlow={netChangeinCash} />
            </tbody>
          ) : (
            <tbody className="empty-table" />
          )}
        </table>
      </div>
    </div>
  );
}

export default CashFlowWidget;



// WEBPACK FOOTER //
// ./src/components/finance/widgets/cash-flow.js